import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, Users, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function POPIACompliance() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-cyan-500/20 rounded-lg">
              <Shield className="h-8 w-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-white">POPIA Compliance</h1>
              <p className="text-slate-400">Protection of Personal Information Act</p>
            </div>
          </div>
          <p className="text-slate-400 mt-2">Last updated: March 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Introduction Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Commitment to POPIA</h2>
          <p className="text-slate-600 mb-4">
            Gantt Account Management (Pty) Ltd is fully committed to complying with the Protection of Personal 
            Information Act 4 of 2013 (POPIA). We recognise that the protection of personal information is both 
            a legal requirement and a fundamental aspect of maintaining trust with our clients.
          </p>
          <p className="text-slate-600">
            This document outlines how we ensure compliance with POPIA in our operations and the measures we 
            have implemented to protect your personal information.
          </p>
        </div>

        {/* Key Principles */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Key POPIA Principles We Follow</h2>
          <div className="grid md:grid-cols-2 gap-6">
            
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900">Accountability</h3>
              </div>
              <p className="text-slate-600 text-sm">
                We have appointed an Information Officer responsible for ensuring POPIA compliance and 
                handling all data protection matters.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-bold text-slate-900">Processing Limitation</h3>
              </div>
              <p className="text-slate-600 text-sm">
                We only collect personal information that is necessary for providing our services and process 
                it lawfully and in a reasonable manner.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="font-bold text-slate-900">Purpose Specification</h3>
              </div>
              <p className="text-slate-600 text-sm">
                We clearly define and communicate the purposes for which we collect personal information 
                and do not use it for unrelated purposes.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Lock className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="font-bold text-slate-900">Security Safeguards</h3>
              </div>
              <p className="text-slate-600 text-sm">
                We implement appropriate technical and organisational measures to protect personal information 
                from loss, damage, or unauthorised access.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Globe className="h-5 w-5 text-cyan-600" />
                </div>
                <h3 className="font-bold text-slate-900">Openness</h3>
              </div>
              <p className="text-slate-600 text-sm">
                We maintain transparency about our data processing activities and make this information 
                readily available to data subjects.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <Shield className="h-5 w-5 text-pink-600" />
                </div>
                <h3 className="font-bold text-slate-900">Data Subject Rights</h3>
              </div>
              <p className="text-slate-600 text-sm">
                We respect and facilitate the exercise of data subject rights, including access, correction, 
                and deletion of personal information.
              </p>
            </div>

          </div>
        </div>

        {/* Detailed Sections */}
        <div className="prose prose-slate max-w-none">
          
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Information We Process</h2>
            <p className="text-slate-600 mb-4">
              In providing our construction management services, we process the following categories of 
              personal information:
            </p>
            
            <div className="bg-slate-100 rounded-lg p-6 mb-4">
              <h4 className="font-bold text-slate-800 mb-2">User Information</h4>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Names, surnames, and professional titles</li>
                <li>Contact details (email, phone numbers)</li>
                <li>Company affiliation and role</li>
                <li>Authentication credentials</li>
              </ul>
            </div>

            <div className="bg-slate-100 rounded-lg p-6 mb-4">
              <h4 className="font-bold text-slate-800 mb-2">Project Information</h4>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>Project documentation and correspondence</li>
                <li>Names of individuals mentioned in project records</li>
                <li>Professional consultant details</li>
                <li>Subcontractor and supplier information</li>
              </ul>
            </div>

            <div className="bg-slate-100 rounded-lg p-6">
              <h4 className="font-bold text-slate-800 mb-2">Technical Information</h4>
              <ul className="list-disc pl-6 text-slate-600 space-y-1">
                <li>IP addresses and device information</li>
                <li>Usage logs and activity data</li>
                <li>Browser and system information</li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Your Rights Under POPIA</h2>
            <p className="text-slate-600 mb-4">
              As a data subject, you have the following rights under POPIA:
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-500 pl-4">
                <h4 className="font-bold text-slate-800">Right to Access</h4>
                <p className="text-slate-600 text-sm">
                  You may request confirmation of whether we hold personal information about you and 
                  request a copy of that information.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-4">
                <h4 className="font-bold text-slate-800">Right to Correction</h4>
                <p className="text-slate-600 text-sm">
                  You may request that we correct or update any inaccurate or incomplete personal 
                  information we hold about you.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-4">
                <h4 className="font-bold text-slate-800">Right to Deletion</h4>
                <p className="text-slate-600 text-sm">
                  You may request that we delete your personal information, subject to any legal 
                  obligations requiring us to retain it.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-4">
                <h4 className="font-bold text-slate-800">Right to Object</h4>
                <p className="text-slate-600 text-sm">
                  You may object to the processing of your personal information on reasonable grounds.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-4">
                <h4 className="font-bold text-slate-800">Right to Lodge a Complaint</h4>
                <p className="text-slate-600 text-sm">
                  You have the right to lodge a complaint with the Information Regulator if you believe 
                  your rights have been infringed.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Security Measures</h2>
            <p className="text-slate-600 mb-4">
              We have implemented comprehensive security measures to protect personal information:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
              <li><strong>Access Control:</strong> Role-based access controls limit data access to authorised personnel</li>
              <li><strong>Authentication:</strong> Secure password requirements and optional two-factor authentication</li>
              <li><strong>Monitoring:</strong> Continuous monitoring for security threats and anomalies</li>
              <li><strong>Backup:</strong> Regular encrypted backups with secure offsite storage</li>
              <li><strong>Training:</strong> Regular staff training on data protection and security</li>
              <li><strong>Incident Response:</strong> Documented procedures for responding to security incidents</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Third-Party Processors</h2>
            <p className="text-slate-600 mb-4">
              We use carefully selected third-party service providers to help deliver our services. 
              All third parties are required to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Process personal information only on our instructions</li>
              <li>Implement appropriate security measures</li>
              <li>Maintain confidentiality of all personal information</li>
              <li>Comply with applicable data protection laws</li>
              <li>Delete or return personal information upon termination of services</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Retention</h2>
            <p className="text-slate-600">
              We retain personal information only for as long as necessary to fulfil the purposes for which 
              it was collected, or as required by law. Construction project records are typically retained 
              for a minimum of 5 years after project completion in accordance with industry requirements 
              and applicable legislation.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">How to Exercise Your Rights</h2>
            <p className="text-slate-600 mb-4">
              To exercise any of your rights under POPIA, please contact our Information Officer:
            </p>
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6">
              <p className="text-slate-800"><strong>Information Officer</strong></p>
              <p className="text-slate-700">Gantt Account Management (Pty) Ltd</p>
              <p className="text-slate-600 mt-2">Email: info@gantt-acc.co.za</p>
              <p className="text-slate-600">Phone: 073 049 1360</p>
              <p className="text-slate-500 text-sm mt-4">
                We will respond to your request within 30 days as required by POPIA.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Information Regulator</h2>
            <p className="text-slate-600 mb-4">
              If you are not satisfied with our response to your request, or believe that your rights 
              have been infringed, you may lodge a complaint with the Information Regulator:
            </p>
            <div className="bg-slate-100 rounded-lg p-6">
              <p className="text-slate-800"><strong>The Information Regulator (South Africa)</strong></p>
              <p className="text-slate-600 mt-2">
                JD House, 27 Stiemens Street<br />
                Braamfontein, Johannesburg, 2001
              </p>
              <p className="text-slate-600 mt-2">
                P.O Box 31533, Braamfontein, Johannesburg, 2017
              </p>
              <p className="text-slate-600 mt-2">
                Email: enquiries@inforegulator.org.za<br />
                Website: www.inforegulator.org.za
              </p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Gantt Account Management. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
